import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { ZOOM_LEVELS, type Zoom } from "../studioV2Url"

export interface ZoomControlProps {
  zoom: Zoom
  onChange: (zoom: Zoom) => void
}

/** "Fit" is the concept's own word for the 1× default — every other level
 *  renders as a bare percentage. */
function zoomLabel(
  zoom: Zoom,
  translate: (key: "MC_DECL_V2_ZOOM_FIT") => string,
): string {
  return zoom === 1
    ? translate("MC_DECL_V2_ZOOM_FIT")
    : `${Math.round(zoom * 100)}%`
}

/**
 * `− 50%|75%|Fit|125%|150%|200% +` — steps through `ZOOM_LEVELS` one notch per
 * click, writing the workspace URL (`?zoom=`) via the caller's `onChange`;
 * `DocumentPane` reads the SAME level back into `scale`, so the control and
 * the rendered page can never disagree about which level is current.
 *
 * Glyphs, not icons — the prototype's `.zb` is styled for a plain "−"/"+"
 * text character (`font:600 14px/1 var(--mono)`, no flex/gap for an icon).
 * A Lucide `<Minus>`/`<Plus>` there was sized by `.sv2-ic` (15px) instead,
 * fighting the button's own 25×23 box. `aria-label` still carries the real
 * accessible name — the glyph alone would be a poor one.
 */
export function ZoomControl({ zoom, onChange }: ZoomControlProps) {
  const { translate } = useTranslation()
  const index = ZOOM_LEVELS.indexOf(zoom)
  const canZoomOut = index > 0
  const canZoomIn = index < ZOOM_LEVELS.length - 1

  return (
    <div className="sv2-zoom">
      <button
        type="button"
        aria-label={translate("MC_DECL_V2_ZOOM_OUT_ARIA")}
        disabled={!canZoomOut}
        onClick={() => canZoomOut && onChange(ZOOM_LEVELS[index - 1])}
        className="sv2-zb"
      >
        −
      </button>
      <span data-testid="zoom-level-label" className="sv2-zlvl">
        {zoomLabel(zoom, translate)}
      </span>
      <button
        type="button"
        aria-label={translate("MC_DECL_V2_ZOOM_IN_ARIA")}
        disabled={!canZoomIn}
        onClick={() => canZoomIn && onChange(ZOOM_LEVELS[index + 1])}
        className="sv2-zb"
      >
        +
      </button>
    </div>
  )
}
